import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import type {FilterObject} from 'nutshell-core/types';

import {ButtonGroup} from 'shells/button-group/button-group';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {ErrorState} from 'shells/error-state/error-state';
import {EditIcon, AudienceIcon} from 'shells/icon';
import {Illustration} from 'shells/illustrations/illustration';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {
    WfFormFragment as Form,
    FormUpdateInput,
    FormFieldUpdateInput,
    FormCreateRulesUpdateInput,
} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../store';

import {FormPageContainer} from '../../forms/form-page/form-main-tab/form-page-container';
import {FormRespondents} from '../../forms/form-page/respondents/form-respondents';
import {getDefaultFieldsForSubmissionsTable} from '../../forms/helpers';
import {PageContainer, PageTitleBar} from '../../layout';
import type {ReportURLParams} from '../../reports/report-types';

import {fetchConnectedFormsSchemaAction} from '../connected-forms-actions';
import {getConnectedFormSchema, getActiveFieldId} from '../connected-forms-selectors';

import {ActivateFormButton} from './activate-form/activate-form-button';
import {ConnectedFormsFieldList} from './connected-forms-field-list';
import {nextStepsCacheUpdate, getStatusInfo} from './helpers';
import {useUpdateWfCreateRules} from './use-update-wf-create-rules';
import {ViewFormButton} from './view-form/view-form-button';

import styles from './connected-forms-page-content.module.css';

export const MAIN_TAB_ID = 'main';
export const SUBMISSIONS_TAB_ID = 'submissions';

type Props = {
    form: Form | null | undefined;
    error?: string | null | undefined;
    isLoading: boolean;
    activeTab: string;
    onSetSelectedTab: (newTab: string) => void;
    onUpdateForm: (arg1: FormUpdateInput) => Promise<any>;
    onUpdateField: (arg1: FormFieldUpdateInput) => Promise<any>;
    refetchForm: () => Promise<any>;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

export function ConnectedFormsPageContent(props: Props) {
    const dispatch = useDispatch();
    const updateCreateRules = useUpdateWfCreateRules();
    React.useEffect(() => {
        // @ts-expect-error
        dispatch(fetchConnectedFormsSchemaAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activeFieldId = useSelector((state: NutshellState) => getActiveFieldId(state));
    const formSchema = useSelector((state: NutshellState) => getConnectedFormSchema(state));

    const formFields =
        props.form && props.form.publishedVersion
            ? props.form.publishedVersion.fields.map((field) => {
                  return {
                      type: field.type,
                      fieldName: field.fieldName,
                      entityName: field.entityName,
                  };
              })
            : undefined;

    const defaultFields = formFields
        ? getDefaultFieldsForSubmissionsTable(formFields).split(',')
        : getDefaultFieldsForSubmissionsTable().split(',');

    const urls = props.form && props.form.mcfxForm && props.form.mcfxForm.urls;

    const tabOptions = [
        {
            id: MAIN_TAB_ID,
            label: 'Form',
            icon: EditIcon,
            href: '#',
            component:
                props.form && props.form.publishedVersion ? (
                    <FormPageContainer
                        formId={props.form.id}
                        title='Form fields'
                        isConnectedForm={true}
                        formContent={
                            <ConnectedFormsFieldList
                                fields={props.form.publishedVersion.fields}
                                onUpdateField={props.onUpdateField}
                                formSchema={formSchema}
                                activeFieldId={activeFieldId}
                            />
                        }
                        createRulesProps={{
                            hasAccountFields: props.form.publishedVersion.hasAccountFields,
                            hasContactFields: props.form.publishedVersion.hasContactFields,
                            hasLeadFields: props.form.publishedVersion.hasLeadFields,
                            createRules: props.form.createRules,
                            updateCreateRules: (input: FormCreateRulesUpdateInput) =>
                                updateCreateRules(input),
                        }}
                        nextStepsProps={{
                            nextSteps: props.form.nextSteps,
                            description:
                                'Notifications will automatically send when this form is submitted.',
                            bodyText: 'When this form is submitted…',
                            slackMessagePreviewMessage: `${props.form.name} was submitted! 🧲`,
                            onUpdateSuccess: nextStepsCacheUpdate,
                        }}
                    />
                ) : (
                    <></>
                ),
        },
        {
            id: SUBMISSIONS_TAB_ID,
            label: 'Submissions',
            icon: AudienceIcon,
            href: '#',
            component:
                props.form && props.form.publishedVersion ? (
                    <FormRespondents
                        formId={props.form.id}
                        defaultFields={defaultFields}
                        numSubmissions={props.form.numSubmissions}
                        activeFilters={props.activeFilters}
                        onParamChange={props.onParamChange}
                        urlParams={props.urlParams}
                        onRemoveAllFilters={props.onRemoveAllFilters}
                        customEmptyStateComponent={
                            <EmptyStateSmall
                                title={
                                    props.form.status === 'NOT_ACTIVATED'
                                        ? 'Activate this form to start collecting submissions'
                                        : 'It looks like this form didn’t have any submissions'
                                }
                                subtitle={
                                    props.form.status === 'NOT_ACTIVATED'
                                        ? 'Once you’ve activated this form, people that have submitted it will appear here'
                                        : 'When your form has one or more submissions, they’ll show up here'
                                }
                                icon={
                                    <div style={{width: '75px'}}>
                                        <Illustration
                                            src='magnifying-glass-empty-state'
                                            width='100%'
                                            height='100%'
                                        />
                                    </div>
                                }
                            />
                        }
                    />
                ) : (
                    <></>
                ),
        },
    ];

    return (
        <PageContainer
            title={props.form && props.form.name.length ? `Form • ${props.form.name}` : 'Form'}
            whiteBackground={true}
            noPadding={props.activeTab === SUBMISSIONS_TAB_ID}
            pageTitleBar={
                <PageTitleBar
                    title={props.form && props.form.name.length ? props.form.name : 'Form'}
                    subheaderPrimaryText=''
                    noMaxWidth={true}
                    breadcrumbs={[
                        {
                            to: '/email-marketing/connected-forms',
                            text: 'Connected forms',
                        },
                    ]}
                    editInPlaceProps={{
                        onChange: (newValue: string | null | undefined) =>
                            props.form
                                ? props.onUpdateForm({
                                      form: {
                                          id: props.form.id,
                                          name: newValue,
                                      },
                                  })
                                : undefined,
                        placeholder: 'Name this form',
                    }}
                    buttons={
                        <ButtonGroup>
                            {NutClientConfig.isInternalLogin && props.form ? (
                                <Link
                                    href={`/support/connected-forms/${props.form.id.split('-')[0]}`}
                                    variant='button-primary'
                                    size='big'
                                    newTab={true}
                                >
                                    Support debug
                                </Link>
                            ) : undefined}
                            <ViewFormButton
                                isDisabled={!urls || !urls.length}
                                formUrls={urls || []}
                            />
                            {props.form &&
                            props.form.publishedVersion &&
                            props.form.status === 'NOT_ACTIVATED' ? (
                                <ActivateFormButton
                                    formId={props.form.id}
                                    fields={props.form.publishedVersion.fields}
                                    formSchema={formSchema}
                                />
                            ) : undefined}
                        </ButtonGroup>
                    }
                />
            }
            controlBar={
                <PageControlBar
                    // @ts-expect-error
                    statusInfo={
                        props.form
                            ? getStatusInfo(props.form, props.activeTab, props.activeFilters)
                            : undefined
                    }
                    tabs={{
                        selectedTab: props.activeTab || MAIN_TAB_ID,
                        options: tabOptions,
                        onChangeTab: (newTab) => {
                            props.onSetSelectedTab(newTab);
                            // We refetch the form when switching between tabs to be sure the
                            // submission data are up to date
                            props.refetchForm();
                        },
                    }}
                />
            }
        >
            <div className='routed-report-content'>
                {props.error ? <ErrorState /> : undefined}
                {!props.error && (props.isLoading || !formSchema) ? <LoadingPage /> : undefined}
                {props.form && props.form.publishedVersion ? (
                    <div className={styles.container}>
                        {/* @ts-expect-error */}
                        <TabbedContent selectedTab={props.activeTab} tabOptions={tabOptions} />
                    </div>
                ) : undefined}
            </div>
        </PageContainer>
    );
}
